import api from '@/shared/utils/apiConfig';
import type { ApiResponse } from '@/shared/types/api';
import type { ExpenseCategory } from './expense.api';

export interface ExpenseCategoryListParams {
  includeInactive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ExpenseCategoryListResult {
  items: ExpenseCategory[];
  total: number;
}

export interface CreateExpenseCategoryPayload {
  name: string;
  sortOrder?: number;
}

export interface UpdateExpenseCategoryPayload {
  name?: string;
  isActive?: boolean;
  sortOrder?: number;
}

const buildListParams = (params: ExpenseCategoryListParams = {}) => ({
  includeInactive: params.includeInactive ? 'true' : undefined,
  search: params.search?.trim() || undefined,
  page: params.page,
  limit: params.limit,
});

export const expenseCategoryAPI = {
  listResult: async (
    params: ExpenseCategoryListParams = {}
  ): Promise<ExpenseCategoryListResult> => {
    const response = await api.get<ApiResponse<ExpenseCategory[]>>('/expense-categories', {
      params: buildListParams(params),
    });
    const items = response.data.data ?? [];
    return {
      items,
      total: response.data.total ?? items.length,
    };
  },

  getAll: async (includeInactive = false): Promise<ApiResponse<ExpenseCategory[]>> => {
    const response = await api.get('/expense-categories', {
      params: includeInactive ? { includeInactive: 'true' } : undefined,
    });
    return response.data;
  },

  create: async (payload: CreateExpenseCategoryPayload): Promise<ApiResponse<ExpenseCategory>> => {
    const response = await api.post('/expense-categories', payload);
    return response.data;
  },

  update: async (
    id: string,
    payload: UpdateExpenseCategoryPayload
  ): Promise<ApiResponse<ExpenseCategory>> => {
    const response = await api.put(`/expense-categories/${id}`, payload);
    return response.data;
  },

  deactivate: async (id: string): Promise<ApiResponse<ExpenseCategory>> => {
    const response = await api.delete(`/expense-categories/${id}`);
    return response.data;
  },
};
